// src/pages/usePlannerData.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBase } from '../utils/getApiBase';
import { resolveEventIndex } from '../utils/dateHelpers';
import { resolveStaffForCopy, notifyStaffCopyConflict } from '../utils/staffCopyConflicts';
import { notifyStaffConflictInfo, requestStaffConflictPrompt } from '../utils/staffConflictPrompt';
import {
    applyStaffCopyToDays,
    eventRefFromCopyArgs,
    findSiblingEventDays,
    formatIsoDayPl,
    splitCopyTargets,
} from '../utils/staffCopySiblings';
import { formatStaffListForStore, normalizeStaffEntries } from '../utils/staffEntry';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minut
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 godziny - maksymalny wiek cache do użycia
const LS_THEATRE = 'planner:theatreCalendarId';
const LS_METADATA = 'planner:cacheMetadata'; // Metadata o cache (kalendarze, timestamp)
const LS_VERSION = 'planner:dataVersion';

// ===== Helpers =====
const monthKeyOfDate = (iso) => String(iso || '').slice(0, 7);
const cacheKey = (monthKey, mainId, theatreId) =>
    `planner:cache:${monthKey}:${mainId || 'none'}:${theatreId || 'none'}`;

const norm = (s) => String(s || '').trim().toLowerCase();
const normName = (s) => String(s || '').trim();
const evKey = (ev) => `${normName(ev?.event)}::${String(ev?.calendarId || '')}`;
const asName = (x) => (typeof x === 'string' ? x : x?.name || '');
// Klucz preferujący gId - używany do dopasowywania eventów z Google Calendar
const evKeyByGId = (ev) => {
    if (ev?.gId && ev?.calendarId) {
        return `gid:${ev.calendarId}::${ev.gId}`;
    }
    return evKey(ev); // fallback do nazwy dla ręcznych eventów
};

const eventIdentity = (e) => (
    e?.gId ? `${e.calendarId || ''}::${e.gId}` : `${e.calendarId || ''}::${normName(e?.event)}`
);

const staffSignature = (staff) => {
    if (!Array.isArray(staff)) return '';
    return staff.map((entry) => {
        if (typeof entry === 'string') {
            return entry.replace(/\s+\(\?\)$/, '').trim().toLowerCase();
        }
        const name = String(entry?.name || '').replace(/\s+\(\?\)$/, '').trim().toLowerCase();
        return name ? `${name}${entry?.tentative ? '?' : ''}` : '';
    }).filter(Boolean).sort().join('|');
};

/** Czy dwa snapshots eventsByDate różnią się obsadą / needed / składem wydarzeń. */
export function eventsByDateDiffer(a = {}, b = {}) {
    const allDays = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
    for (const day of allDays) {
        const listA = Array.isArray(a[day]) ? a[day] : [];
        const listB = Array.isArray(b[day]) ? b[day] : [];
        if (listA.length !== listB.length) return true;
        const mapB = new Map(listB.map((ev) => [eventIdentity(ev), ev]));
        for (const ev of listA) {
            const other = mapB.get(eventIdentity(ev));
            if (!other) return true;
            if (staffSignature(ev.staff) !== staffSignature(other.staff)) return true;
            if (Number(ev.needed || 0) !== Number(other.needed || 0)) return true;
        }
    }
    return false;
}

// prefer: DB > other (dla metadanych); fetched dostarcza żywe instancje
// WAŻNE: Używa gId jako klucza głównego dla eventów z Google Calendar, aby obsłużyć zmiany nazwy
// prefer: DB > other (dla metadanych); fetched dostarcza żywe instancje
// WAŻNE: Używa gId jako klucza głównego dla eventów z Google Calendar, aby obsłużyć zmiany nazwy
// calendarsToPrune - opcjonalna lista kalendarzy do sprawdzenia (jeśli wydarzenie z DB nie ma odpowiednika w fetched, zostanie pominięte)
function deepMergeEvents(db = {}, other = {}, calendarsToPrune = null) {
    const out = {};
    const allDays = new Set([...Object.keys(db || {}), ...Object.keys(other || {})]);
    const calendarsToPruneSet = calendarsToPrune ? new Set(calendarsToPrune) : null;

    for (const day of allDays) {
        const dbList = Array.isArray(db[day]) ? db[day] : [];
        const otList = Array.isArray(other[day]) ? other[day] : [];

        // Mapa używająca gId jako klucza (jeśli dostępne), w przeciwnym razie nazwy
        const map = new Map();
        const gIdMap = new Map(); // Dodatkowa mapa dla szybkiego wyszukiwania po gId
        
        // Najpierw dodaj eventy z fetched (other) - mają aktualne nazwy
        for (const e of otList) {
            const key = evKeyByGId(e);
            map.set(key, { ...e });
            if (e.gId && e.calendarId) {
                gIdMap.set(`${e.calendarId}::${e.gId}`, e);
            }
        }

        // Teraz merge z eventami z DB
        for (const e of dbList) {
            const key = evKeyByGId(e);
            
            // Sprawdź czy istnieje event z tym samym gId (nawet jeśli nazwa się zmieniła)
            let matchedEvent = null;
            let matchMethod = 'none';
            if (e.gId && e.calendarId) {
                const gIdKey = `${e.calendarId}::${e.gId}`;
                matchedEvent = gIdMap.get(gIdKey);
                if (matchedEvent) {
                    matchMethod = 'gId';
                } else {
                    // Debug: sprawdź dlaczego nie znalazł
                    const allGIdKeys = Array.from(gIdMap.keys());
                    const similarKeys = allGIdKeys.filter(k => k.includes(e.gId) || k.includes(e.calendarId));
                }
            }
            
            // Jeśli nie znaleziono po gId, spróbuj po nazwie (dla ręcznych eventów)
            if (!matchedEvent && map.has(key)) {
                matchedEvent = map.get(key);
                if (matchedEvent) matchMethod = 'name';
            }
            
            
            if (matchedEvent) {
                // Merge: zachowaj staff/needed z DB, ale użyj nowej nazwy z fetched
                // Priorytet dla staff: DB (e.staff) > fetched (matchedEvent.staff) > []
                // WAŻNE: Sprawdzamy czy e.staff istnieje (nie undefined/null) i czy jest tablicą
                // WAŻNE: Zachowaj pytajniki (tentative) z DB
                let mergedStaff = [];
                const dbHasStaff = e.staff !== undefined && e.staff !== null;
                const dbStaffArray = Array.isArray(e.staff) ? e.staff : [];
                const fetchedHasStaff = Array.isArray(matchedEvent.staff) && matchedEvent.staff.length > 0;
                
                if (dbHasStaff && dbStaffArray.length > 0) {
                    // Jeśli DB ma staff, użyj go (zachowuje pytajniki i inne metadane)
                    mergedStaff = [...dbStaffArray];
                } else if (fetchedHasStaff) {
                    // Jeśli DB nie ma, ale fetched ma, użyj fetched
                    mergedStaff = [...matchedEvent.staff];
                }
                // W przeciwnym razie zostaje pusta tablica []
                
                
                map.set(evKeyByGId(matchedEvent), {
                    ...matchedEvent, // nowa nazwa z fetched
                    staff: mergedStaff, // WAŻNE: staff musi być PO spread, żeby nadpisać
                    needed: Number.isFinite(Number(e.needed)) && Number(e.needed) >= 1 
                        ? Number(e.needed) 
                        : (matchedEvent.needed || 1),
                    color: e.color || matchedEvent.color,
                    notes: e.notes ?? matchedEvent.notes,
                    gId: matchedEvent.gId || e.gId,
                    calendarId: matchedEvent.calendarId || e.calendarId,
                });
            } else {
                // Ręczny event lub wydarzenie z DB, które nie ma odpowiednika w fetched
                // WAŻNE: Nie dodawaj wydarzeń z DB, które nie są w fetched, jeśli mają calendarId
                // (oznacza to, że zostały usunięte z Google Calendar)
                if (e?.calendarId && calendarsToPrune && calendarsToPrune.includes(e.calendarId)) {
                    // To wydarzenie z Google Calendar, które nie jest już w fetched - pomiń
                    continue;
                }
                // Ręczny event (bez calendarId) - dodaj bez zmian
                map.set(key, { ...e });
            }
        }
        
        // Usuń duplikaty - jeśli mamy event z gId, usuń wszystkie inne eventy z tym samym gId ale inną nazwą
        // WAŻNE: Priorytetyzuj eventy z DB (mają staff) nad eventami z fetched
        const finalList = [];
        const seenGIds = new Map(); // Map<gIdKey, {index, hasStaff}> - przechowuje info o eventach
        
        for (const ev of Array.from(map.values())) {
            if (ev.gId && ev.calendarId) {
                const gIdKey = `${ev.calendarId}::${ev.gId}`;
                const existing = seenGIds.get(gIdKey);
                const currentHasStaff = ev.staff && Array.isArray(ev.staff) && ev.staff.length > 0;
                
                if (existing) {
                    // Sprawdź czy obecny event ma staff, a istniejący nie ma
                    const existingEv = finalList[existing.index];
                    const existingHasStaff = existing.hasStaff;
                    
                    if (currentHasStaff && !existingHasStaff) {
                        // Obecny event ma staff, a istniejący nie - zastąp
                        finalList[existing.index] = ev;
                        seenGIds.set(gIdKey, { index: existing.index, hasStaff: true });
                    } else {
                        // Pomiń duplikat - istniejący ma priorytet (lub oba nie mają staff)
                        continue;
                    }
                } else {
                    const hasStaff = ev.staff && Array.isArray(ev.staff) && ev.staff.length > 0;
                    seenGIds.set(gIdKey, { index: finalList.length, hasStaff });
                    finalList.push(ev);
                }
            } else {
                // Event bez gId - zawsze dodaj
                finalList.push(ev);
            }
        }
        
        out[day] = finalList;
    }
    return out;
}

// PRUNING po „dzień + (calendarId, nazwa)”; wspiera alive/cancelled z rozszerzonego fetchu
function pruneDbByFetched(dbEvents = {}, calendarsToPrune = [], fetchedAll = {}, opts = {}) {
    const calSet = new Set((calendarsToPrune || []).filter(Boolean));
    if (!calSet.size) return dbEvents;

    const {
        aliveTriples = new Set(),       // `${calId}::${gId}::${day}`
        aliveNameDays = new Set(),      // `${calId}::${normName}::${day}`
        cancelledNameDays = new Set(),  // `${calId}::${normName}::${day}`
    } = opts;

    const out = {};
    for (const [day, arr] of Object.entries(dbEvents || {})) {
        const fetchedSetForDay = new Set(
            ((fetchedAll[day] || []).map(e => `${norm(e.event)}::${e.calendarId || ''}`))
        );

        const next = (arr || []).filter((e) => {
            const cal = e?.calendarId || '';
            if (!cal) return true;           // ręczne
            if (!calSet.has(cal)) return true; // inne kalendarze

            // Najpierw sprawdź czy wydarzenie jest w cancelledNameDays (zostało anulowane)
            const nameDayKey = `${cal}::${norm(e?.event)}::${day}`;
            if (cancelledNameDays.has(nameDayKey)) {
                return false; // Wydarzenie zostało anulowane - usuń
            }

            // Jeśli mamy aliveTriples lub aliveNameDays, użyj ich do weryfikacji
            if (aliveTriples.size > 0 || aliveNameDays.size > 0) {
                if (e?.gId) {
                    // Sprawdź po gId (najbardziej niezawodne)
                    const gIdKey = `${cal}::${e.gId}::${day}`;
                    if (aliveTriples.has(gIdKey)) {
                        return true; // Wydarzenie jest żywe
                    }
                    // Jeśli nie ma w aliveTriples, ale ma gId, oznacza to że zostało usunięte
                    return false;
                }
                // Sprawdź po nazwie (dla wydarzeń bez gId)
                if (aliveNameDays.has(nameDayKey)) {
                    return true; // Wydarzenie jest żywe
                }
                // Jeśli nie ma w aliveNameDays, oznacza to że zostało usunięte
                return false;
            }

            // prosty tryb – zgodność z fetched na ten dzień (fallback)
            return fetchedSetForDay.has(`${norm(e?.event)}::${cal}`);
        });

        out[day] = next;
    }
    return out;
}

function hydrateStaffFromRoles(eventsByDate = {}, eventStaffRoles = {}) {
    // Tworzymy płytką kopię struktur dni → listy, aby nie mutować oryginału
    const hydrated = {};
    const roleRank = (role) => {
        if (!role) return 99;
        const r = String(role).toLowerCase();
        if (r === 'stage_manager') return 0;
        if (r === 'inspicjent') return 1;
        return 2;
    };

    for (const [date, events] of Object.entries(eventsByDate || {})) {
        const rolesForDate = eventStaffRoles?.[date];
        if (!Array.isArray(events) || !rolesForDate || !Object.keys(rolesForDate).length) {
            hydrated[date] = Array.isArray(events) ? events.map((ev) => ({ ...ev })) : [];
            continue;
        }

        hydrated[date] = events.map((event) => {
            // Jeśli event już ma obsadę, nic nie zmieniaj
            const existingStaff = Array.isArray(event?.staff) ? event.staff.filter(Boolean) : [];
            if (existingStaff.length > 0) {
                return { ...event };
            }

            const rolesForEvent = rolesForDate[event?.event];
            if (!rolesForEvent || !Object.keys(rolesForEvent).length) {
                return { ...event };
            }

            const staffFromRoles = Object.entries(rolesForEvent)
                .map(([name, role]) => ({
                    name,
                    role: role || '',
                }))
                .sort((a, b) => roleRank(a.role) - roleRank(b.role) || a.name.localeCompare(b.name, 'pl', { sensitivity: 'base' }))
                .map(({ name }) => name)
                .filter(Boolean);

            if (staffFromRoles.length === 0) {
                return { ...event };
            }

            return {
                ...event,
                staff: staffFromRoles,
            };
        });
    }

    return hydrated;
}

async function fetchJSON(url, opts = {}) {
    const res = await fetch(url, { ...opts });
    const contentType = res.headers?.get?.('content-type') || '';
    let payload = null;

    if (contentType.includes('application/json')) {
        try {
            payload = await res.json();
        } catch {
            payload = null;
        }
    } else {
        payload = await res.text();
    }

    if (!res.ok) {
        const message = typeof payload === 'object' && payload !== null
            ? payload?.message || payload?.error || `${res.status} ${res.statusText}`
            : `${res.status} ${res.statusText}`;
        const err = new Error(`${message} @ ${url}`);
        err.status = res.status;
        if (payload && typeof payload === 'object') {
            err.code = payload.error || payload.code;
            err.details = payload;
        }
        throw err;
    }

    return typeof payload === 'object' && payload !== null ? payload : {};
}

function monthsFromEvents(events = {}) {
    const set = new Set();
    Object.keys(events || {}).forEach((d) => set.add(monthKeyOfDate(d)));
    return Array.from(set);
}

function setCacheForMonths(events, mainId, theatreId) {
    const months = monthsFromEvents(events);
    months.forEach((mk) => {
        try {
            localStorage.setItem(
                cacheKey(mk, mainId, theatreId),
                JSON.stringify({ ts: Date.now(), events })
            );
        } catch { }
    });
}

function mergeFetchedEventsParts(parts = []) {
    const out = {};
    const aliveTriples = new Set();
    const aliveNameDays = new Set();
    const cancelledNameDays = new Set();

    for (const p of (parts || [])) {
        if (!p) continue;
        const evs = p.events || {};
        for (const [day, arr] of Object.entries(evs)) {
            if (!out[day]) out[day] = [];
            if (Array.isArray(arr) && arr.length) {
                out[day].push(...arr);
            }
        }
        for (const t of (p.aliveTriples || [])) aliveTriples.add(t);
        for (const t of (p.aliveNameDays || [])) aliveNameDays.add(t);
        for (const t of (p.cancelledNameDays || [])) cancelledNameDays.add(t);
    }

    return { events: out, aliveTriples, aliveNameDays, cancelledNameDays };
}

// ===== Hook =====
export function usePlannerData(isoInput, stringToColor, onDataUpdated) {
    const [eventsByDate, setEventsByDate] = useState({});
    const [employees, setEmployees] = useState([]);
    const [calendarList, setCalendarList] = useState([]);
    const [selectedCalendar, setSelectedCalendar] = useState('');
    const [unavailableByDate, setUnavailableByDate] = useState({});
    const [loading, setLoading] = useState(true);
    const [theatreCalendar, setTheatreCalendar] = useState('');
    const [dataReady, setDataReady] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false); // Wskaźnik synchronizacji w tle
    const [firestoreQuotaExceeded, setFirestoreQuotaExceeded] = useState(false);
    const loadEventsRef = useRef(null); // Ref do funkcji loadEvents dla BroadcastChannel
    const instanceIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const existing = sessionStorage.getItem('planner:instanceId');
            if (existing) {
                instanceIdRef.current = existing;
            } else {
                sessionStorage.setItem('planner:instanceId', instanceIdRef.current);
            }
            // zapamiętaj ostatnie użyte ID w localStorage (fallback dla SW)
            localStorage.setItem('planner:lastInstanceId', instanceIdRef.current);
        } catch {
            // ignore
        }
    }, []);
    const dataVersionRef = useRef(0);
    const pendingVersionRef = useRef(null);
    const isFetchingRef = useRef(false);
    const queuedForceFetchRef = useRef(false);
    const lastFailedFetchRef = useRef(0);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const storedVersion = Number(localStorage.getItem(LS_VERSION) || 0);
            if (storedVersion) {
                dataVersionRef.current = storedVersion;
            }
        } catch {
            // ignore
        }
    }, []);

    // iso helper -> zawsze funkcja
    const isoFn = useCallback((input) => {
        try {
            if (typeof isoInput === 'function') return isoInput(input);
            if (input) {
                const d = new Date(input);
                const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
                return local.toISOString().slice(0, 10);
            }
            if (typeof isoInput === 'string' && isoInput) return isoInput;
            const now = new Date();
            const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
            return local.toISOString().slice(0, 10);
        } catch {
            return typeof isoInput === 'string' ? isoInput : '';
        }
    }, [isoInput]);

    const normalizeEmployees = (list) =>
        (list || []).map((e) => (typeof e === 'string' ? { name: e, isTheatre: false } : e));

    // ⬇️ teraz zapisujemy też calendarList
    const saveData = async (
        eventsByDateArg,
        employeesArg,
        selectedCalendarArg,
        unavailableArg,
        theatreCalendarArg,
        calendarListArg
    ) => {
        try {
            const clientId = instanceIdRef.current || '';
            await fetch(`${getApiBase()}/api/saveData`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(clientId ? { 'x-client-id': clientId } : {}),
                },
                body: JSON.stringify({
                    // eventsByDate TYLKO gdy caller jawnie je przekazał.
                    // Inaczej zapis pracowników/kalendarza nadpisywał obsady
                    // starym snapshotem z cache (wczorajsze zmiany znikały).
                    ...(eventsByDateArg !== undefined ? { eventsByDate: eventsByDateArg } : {}),
                    employees: employeesArg ?? employees,
                    selectedCalendar: selectedCalendarArg ?? selectedCalendar,
                    unavailableByDate: unavailableArg ?? unavailableByDate,
                    theatreCalendar: theatreCalendarArg ?? theatreCalendar,
                    calendarList: calendarListArg ?? calendarList,        // <── KLUCZ
                }),
            });
        } catch (err) {
            // zapis nie blokuje UI
        }
    };

    const normalizeFetch = (res) => {
        if (!res) return { events: {}, aliveTriples: new Set(), aliveNameDays: new Set(), cancelledNameDays: new Set() };
        if (res.events) {
            return {
                events: res.events || {},
                aliveTriples: res.aliveTriples || new Set(),
                aliveNameDays: res.aliveNameDays || new Set(),
                cancelledNameDays: res.cancelledNameDays || new Set(),
            };
        }
        return { events: res, aliveTriples: new Set(), aliveNameDays: new Set(), cancelledNameDays: new Set() };
    };

    const loadEvents = async (forceSelectedCalId, { forceFetch = false } = {}) => {
        if (isFetchingRef.current) {
            if (forceFetch) {
                queuedForceFetchRef.current = true;
            }
            return;
        }
        // FAZA 1: Szybkie wczytanie z cache (nie blokuje UI)
        let cacheLoaded = false;
        let cachedEventsSnapshot = null;
        let effectiveCalendarId = '';
        let theatreId = '';
        const pendingRemote = Boolean(pendingVersionRef.current);
        let theatreFromLs = '';
        
        try {
            try { theatreFromLs = localStorage.getItem(LS_THEATRE) || ''; } catch { }
            // Spróbuj wczytać metadata cache
            let cacheMetadata = null;
            try {
                cacheMetadata = JSON.parse(localStorage.getItem(LS_METADATA) || 'null');
            } catch { }
            
            // Jeśli mamy metadata, użyj jej do szybkiego startu
            if (cacheMetadata && cacheMetadata.selectedCalendar) {
                effectiveCalendarId = (forceSelectedCalId || '').trim() || 
                                     cacheMetadata.selectedCalendar || '';
                // Preferuj LS_THEATRE (najnowszy wybór użytkownika) nad cacheMetadata
                theatreId = (theatreFromLs || '').trim() || cacheMetadata.theatreCalendar || '';
                
                const today = isoFn(new Date());
                const mk = monthKeyOfDate(today);
                const ck = cacheKey(mk, effectiveCalendarId, theatreId);
                
                const cached = JSON.parse(localStorage.getItem(ck) || 'null');
                if (cached && cached.events && cached.ts) {
                    const cacheAge = Date.now() - cached.ts;
                    // Użyj cache jeśli ma mniej niż 24h (nawet jeśli nie jest świeży)
                    if (cacheAge < CACHE_MAX_AGE_MS) {
                        // Cache może zawierać usunięte wydarzenia - nie przekazuj calendarsToPrune tutaj,
                        // bo cache jest tylko tymczasowy i zostanie zastąpiony przez świeże dane z Google
                        const mergedWarm = deepMergeEvents({}, cached.events);
                        cachedEventsSnapshot = mergedWarm;
                        setEventsByDate(mergedWarm);
                        
                        // Wczytaj employees z cache jeśli jest
                        if (cacheMetadata.employees) {
                            setEmployees(normalizeEmployees(cacheMetadata.employees));
                        }
                        if (cacheMetadata.calendarList) {
                            setCalendarList(cacheMetadata.calendarList);
                        }
                        if (cacheMetadata.unavailableByDate) {
                            setUnavailableByDate(cacheMetadata.unavailableByDate);
                        }
                        
                        setSelectedCalendar(effectiveCalendarId);
                        setTheatreCalendar(theatreId);
                        setDataReady(true);
                        cacheLoaded = true;
                        setLoading(false); // UI gotowe, ale sync w tle
                    }
                }
            }
        } catch (err) {
            // Ignoruj błędy cache
        }

        const now = Date.now();
        if (pendingRemote && lastFailedFetchRef.current && (now - lastFailedFetchRef.current) < 60000 && !forceFetch) {
            setLoading(false);
            setIsSyncing(false);
            return;
        }

        // Cache jest tylko do natychmiastowego rysowania. Zawsze dociągamy Firestore,
        // inaczej wczorajszy zapis z innego urządzenia/karty nigdy nie trafi do UI
        // (stary cache < 24h blokował fetch).
        isFetchingRef.current = true;

        // FAZA 2: W tle - pobierz świeże dane z DB i Google
        let fetchSucceeded = false;
        (async () => {
            try {
                setLoading(!cacheLoaded); // Pokaż loading tylko jeśli nie mamy cache
                if (cacheLoaded) {
                    setIsSyncing(true); // Pokaż że trwa sync w tle
                }
                
                // 1) snapshot z bazy
                let snap = null;
                try {
                    snap = await fetchJSON(`${getApiBase()}/api/loadData`);
                    if (firestoreQuotaExceeded) {
                        setFirestoreQuotaExceeded(false);
                    }
                } catch (err) {
                    if (err?.code === 'firestore-quota' || err?.status === 429) {
                        setFirestoreQuotaExceeded(true);
                        setLoading(false);
                        setIsSyncing(false);
                        setDataReady(true);
                        return;
                    }
                    throw err;
                }

                // 2) token Google (opcjonalny)
                let tokenResp = null;
                try {
                    tokenResp = await fetchJSON(`${getApiBase()}/api/getAccessToken`);
                } catch {
                    tokenResp = null;
                }

                const access_token = tokenResp?.access_token || '';
                const dbEventsRaw = snap?.eventsByDate || {};
                const dbEventStaffRoles = snap?.eventStaffRoles || {};
                const dbEventsHydrated = hydrateStaffFromRoles(dbEventsRaw, dbEventStaffRoles);
                const dbEmployees = normalizeEmployees(snap?.employees || []);
                const dbSelected = String(snap?.selectedCalendar || '').trim();
                const dbUnavailable = snap?.unavailableByDate || {};
                const dbTheatre = String(snap?.theatreCalendar || '').trim();
                const dbCalendars = Array.isArray(snap?.calendarList) ? snap.calendarList : [];
                const incomingVersion = Number(snap?.version || snap?.dataVersion || 0);

                if (incomingVersion) {
                    dataVersionRef.current = incomingVersion;
                    pendingVersionRef.current = null;
                    try {
                        localStorage.setItem(LS_VERSION, String(incomingVersion));
                    } catch {
                        // ignore storage failures
                    }
                } else if (pendingRemote) {
                    pendingVersionRef.current = null;
                }

                setCalendarList(dbCalendars);
                setEmployees(dbEmployees);

                // WYBÓR GŁÓWNEGO (nadpisz cache jeśli DB ma inne)
                effectiveCalendarId =
                    (forceSelectedCalId || '').trim() ||
                    (selectedCalendar || '').trim() ||
                    dbSelected ||
                    (dbCalendars[0]?.id || '');
                setSelectedCalendar(effectiveCalendarId);

                // Theatre
                // WAŻNE: theatreId może być "stare" z cacheMetadata, więc zawsze preferuj LS_THEATRE / stan / DB
                if (!theatreFromLs) {
                    try { theatreFromLs = localStorage.getItem(LS_THEATRE) || ''; } catch { }
                }
                theatreId =
                    (theatreCalendar || '').trim() ||
                    (theatreFromLs || '').trim() ||
                    (theatreId || '').trim() ||
                    dbTheatre ||
                    '';
                setTheatreCalendar(theatreId);

                // 2) Merge cache z DB (DB ma priorytet dla staff/needed)
                const GRACE_MS = 5000;
                const inGracePeriod = lastLocalPersistAtRef.current && (Date.now() - lastLocalPersistAtRef.current) < GRACE_MS;
                    const today = isoFn(new Date());
                const mk = monthKeyOfDate(today);
                const ck = cacheKey(mk, effectiveCalendarId, theatreId);
                
                
                let currentEvents = dbEventsHydrated;
                if (cachedEventsSnapshot && dbEventsHydrated && !inGracePeriod) {
                    // Scalamy cache z DB - DB ma priorytet dla staff/needed
                    currentEvents = deepMergeEvents(dbEventsHydrated, cachedEventsSnapshot);
                }
                if (!inGracePeriod && eventsByDateDiffer(cachedEventsSnapshot || {}, currentEvents)) {
                    setEventsByDate(currentEvents);
                    setUnavailableByDate(Object.keys(dbUnavailable || {}).length ? dbUnavailable : {});
                } else if (!cacheLoaded && !inGracePeriod) {
                    setEventsByDate(currentEvents);
                    setUnavailableByDate(Object.keys(dbUnavailable || {}).length ? dbUnavailable : {});
                } else if (!inGracePeriod) {
                    setUnavailableByDate(Object.keys(dbUnavailable || {}).length ? dbUnavailable : {});
                }

                // Zapisz do cache (lokalnie)
                // Sync eventów odbywa się TYLKO przez webhook push (googleWebhook) + incremental sync.
                try {
                    localStorage.setItem(ck, JSON.stringify({ ts: Date.now(), events: currentEvents }));
                    localStorage.setItem(LS_METADATA, JSON.stringify({
                        selectedCalendar: effectiveCalendarId,
                        theatreCalendar: theatreId,
                        employees: dbEmployees,
                        calendarList: dbCalendars,
                        unavailableByDate: dbUnavailable,
                        dataVersion: incomingVersion || dataVersionRef.current || 0,
                        timestamp: Date.now()
                    }));
                } catch { }

                if (!cacheLoaded) {
                    setDataReady(true);
                }
                fetchSucceeded = true;
                lastFailedFetchRef.current = 0;
                // Powiadomienia ładuje panel: raz przy starcie i po DATA_VERSION_UPDATE.
                // Nie wołamy ich stąd — każdy loadEvents dokładał drugie getNotifications.
            } catch (err) {
                // Jeśli nie mamy cache i sync się nie udał, pokaż błąd ale nie blokuj
                if (!cacheLoaded) {
                    setDataReady(true);
                }
                lastFailedFetchRef.current = Date.now();
            } finally {
                if (pendingRemote && fetchSucceeded) {
                    pendingVersionRef.current = null;
                }
                setLoading(false);
                setIsSyncing(false); // Zakończ sync
                isFetchingRef.current = false;
                if (queuedForceFetchRef.current) {
                    queuedForceFetchRef.current = false;
                    loadEvents(undefined, { forceFetch: true });
                }
            }
        })();
    };

    // ───── Admin: API do zarządzania listą i wyborem kalendarzy ─────
    const setCalendarListAndSave = async (nextList) => {
        const sanitized = (nextList || []).map((c) => ({
            id: String(c.id || '').trim(),
            name: String(c.name || '').trim() || String(c.id || '').trim(),
        })).filter((c) => c.id);
        setCalendarList(sanitized);
        await saveData(undefined, undefined, undefined, undefined, undefined, sanitized);
    };

    const replaceCalendarsAndReload = async ({ list, mainId, theatreId }) => {
        const sanitized = (list || []).map((c) => ({
            id: String(c.id || '').trim(),
            name: String(c.name || '').trim() || String(c.id || '').trim(),
        })).filter((c) => c.id);

        setCalendarList(sanitized);
        setSelectedCalendar(mainId || '');
        setTheatreCalendar(theatreId || '');

        await saveData(undefined, undefined, mainId || '', undefined, theatreId || '', sanitized);
        // po zapisie pobierz tylko wybrany kalendarz
        await loadEvents(mainId || '', { forceFetch: true });
    };

    const changeTheatreCalendar = async (calendarId) => {
        const v = String(calendarId || '').trim();
        setTheatreCalendar(v);
        try { localStorage.setItem(LS_THEATRE, v); } catch { }
        // Zaktualizuj metadata cache, żeby wybór nie „cofał się” po odświeżeniu
        try {
            const existing = JSON.parse(localStorage.getItem(LS_METADATA) || 'null') || {};
            localStorage.setItem(LS_METADATA, JSON.stringify({ ...existing, theatreCalendar: v, timestamp: Date.now() }));
        } catch { }
        await saveData(undefined, undefined, undefined, undefined, v, undefined);
        // Po zmianie teatru dociągnij świeże dane z Google (main + theatre)
        await loadEvents(undefined, { forceFetch: true });
    };

    const changeCalendarAndReload = async (calendarId) => {
        const v = String(calendarId || '').trim();
        setSelectedCalendar(v);
        await saveData(undefined, undefined, v, undefined, undefined, undefined);
        await loadEvents(v, { forceFetch: true });
    };

    // ====== Mutatory zdarzeń/unavailable ======
    // Debounce dla persistAndCache, aby uniknąć zbyt częstych zapisów
    const persistAndCacheTimeoutRef = useRef(null);
    const persistAndCachePendingRef = useRef(null);
    /** Ostatni moment wysłania naszej własnej aktualizacji (broadcast z sourceId). Używane przy odbiorze data-updated bez sourceId, żeby nie nadpisywać świeżej lokalnej zmiany starą odpowiedzią API. */
    const lastLocalPersistAtRef = useRef(0);

    const persistAndCache = async (nextEvents) => {
        lastLocalPersistAtRef.current = Date.now();
        setEventsByDate(nextEvents);
        
        // Zapisz do cache natychmiast (lokalnie)
        setCacheForMonths(nextEvents, selectedCalendar, theatreCalendar);
        
        // Debounce zapisu do serwera - czekaj 500ms przed zapisem
        if (persistAndCacheTimeoutRef.current) {
            clearTimeout(persistAndCacheTimeoutRef.current);
        }
        
        persistAndCachePendingRef.current = nextEvents;
        
        persistAndCacheTimeoutRef.current = setTimeout(async () => {
            const eventsToSave = persistAndCachePendingRef.current;
            if (!eventsToSave) return;
            
            try {
                await saveData(eventsToSave);
                
                // Wyślij sygnał do innych instancji (karty) o aktualizacji danych
                if (typeof BroadcastChannel !== 'undefined') {
                    lastLocalPersistAtRef.current = Date.now();
                    const channel = new BroadcastChannel('planner-updates');
                    channel.postMessage({
                        type: 'data-updated',
                        timestamp: Date.now(),
                        source: 'staff-update',
                        sourceId: instanceIdRef.current,
                    });
                    channel.close();
                }
            } catch (e) {
                // Ignoruj błędy zapisu
            }
            
            persistAndCachePendingRef.current = null;
        }, 500); // 500ms debounce
    };

    const updateEventColor = (date, eventOrIndex, color, calendarId, save = true) => {
        setEventsByDate((prev) => {
            const copy = { ...(prev || {}) };
            const list = Array.isArray(copy[date]) ? [...copy[date]] : [];
            let idx = -1;
            
            if (typeof eventOrIndex === 'number') {
                idx = eventOrIndex;
            } else if (typeof eventOrIndex === 'object' && eventOrIndex?.gId && calendarId) {
                // Najpierw spróbuj znaleźć po gId
                idx = list.findIndex(
                    (ev) => ev.gId === eventOrIndex.gId && ev.calendarId === calendarId
                );
            }
            
            // Fallback: szukaj po nazwie
            if (idx === -1 && typeof eventOrIndex !== 'number') {
                idx = list.findIndex(
                    (ev) =>
                        normName(ev.event) === normName(eventOrIndex) &&
                        (calendarId ? ev.calendarId === calendarId : true)
                );
            }
            
            if (idx < 0 || !list[idx]) return prev;
            const ev = { ...list[idx], color };
            list[idx] = ev;
            copy[date] = list;
            if (save) persistAndCache(copy);
            return copy;
        });
    };

    const updateEventStaff = (date, eventName, newStaff, calendarId, save = true) => {
        const eventObj = typeof eventName === 'object' ? eventName : null;
        const eventNameStr = eventObj ? (eventObj.event || eventObj.name || '') : String(eventName || '');
        const name = normName(eventNameStr);
        const gId = eventObj?.gId;
        
        setEventsByDate((prev) => {
            const copy = { ...(prev || {}) };
            const list = Array.isArray(copy[date]) ? [...copy[date]] : [];
            
            // Najpierw spróbuj znaleźć po gId (jeśli eventName to obiekt z gId)
            let idx = -1;
            if (gId && calendarId) {
                idx = list.findIndex(
                    (ev) => ev.gId === gId && ev.calendarId === calendarId
                );
            }
            
            // Fallback: szukaj po nazwie (dla ręcznych eventów lub gdy gId nie jest dostępne)
            if (idx === -1 && name) {
                idx = list.findIndex(
                    (ev) => normName(ev.event) === name && (calendarId ? ev.calendarId === calendarId : true)
                );
            }
            
            // Jeśli nadal nie znaleziono i calendarId jest podane, spróbuj bez calendarId (dla kompatybilności)
            if (idx === -1 && calendarId && name) {
                idx = list.findIndex(
                    (ev) => normName(ev.event) === name && !ev.calendarId
                );
                if (idx >= 0) {
                    // Znaleziono event bez calendarId - dodaj calendarId
                    const ev = { ...list[idx] };
                    ev.calendarId = calendarId;
                    list[idx] = ev;
                }
            }
            
            if (idx === -1) {
                return prev;
            }
            const ev = { ...list[idx] };
            const oldStaff = ev.staff || [];
            const newStaffArray = Array.isArray(newStaff) ? [...newStaff] : [];
            
            // Jeśli usuwamy osobę (nowy staff jest krótszy), zwiększ needed o różnicę
            const oldCount = oldStaff.length;
            const newCount = newStaffArray.length;
            const removedCount = oldCount - newCount;
            
            // Pobierz currentNeeded PRZED zmianą staff (ważne dla poprawnego obliczenia)
            const currentNeeded = Number.isFinite(Number(ev.needed)) && Number(ev.needed) >= 1 
                ? Number(ev.needed) 
                : Math.max(1, oldCount);
            
            // Teraz zaktualizuj staff
            ev.staff = newStaffArray;
            
            if (removedCount > 0) {
                // Gdy usuwamy osobę, zwiększamy needed tylko wtedy, gdy:
                // 1. Po usunięciu nadal mamy tyle samo lub więcej osób niż needed (newCount >= currentNeeded)
                //    To oznacza, że usunęliśmy osobę, ale nadal brakuje miejsc
                // 2. Zawsze upewniamy się, że needed >= newCount (needed jest co najmniej równe liczbie przypisanych osób)
                
                let newNeeded = currentNeeded;
                
                // Jeśli po usunięciu nadal mamy tyle samo lub więcej osób niż needed, zwiększ needed o 1
                if (newCount >= currentNeeded) {
                    newNeeded = currentNeeded + 1;
                } else {
                    // Jeśli po usunięciu mamy mniej osób niż needed, upewnij się że needed >= newCount
                    newNeeded = Math.max(currentNeeded, newCount);
                }
                
                // Ustaw needed tylko jeśli faktycznie się zmieniło
                if (ev.needed !== newNeeded) {
                    ev.needed = newNeeded;
                }
            } else if (!Number.isFinite(Number(ev.needed)) || Number(ev.needed) < 1) {
                // Jeśli needed nie jest ustawione, ustaw na podstawie aktualnego staff
                ev.needed = Math.max(1, newCount || 1);
            } else {
                // Gdy dodajemy osobę (removedCount <= 0), upewnij się że needed >= newCount
                const minNeeded = Math.max(1, newCount);
                if (ev.needed < minNeeded) {
                    ev.needed = minNeeded;
                }
            }
            
            // Zapewnij że calendarId jest ustawione
            if (calendarId && !ev.calendarId) {
                ev.calendarId = calendarId;
            }
            list[idx] = ev;
            copy[date] = list;

            // 🔹 Loguj zmianę przypisań do wydarzenia (w tle, nie blokuje)
            if (save && oldCount !== newCount) {
                // Pobierz dane użytkownika z localStorage
                try {
                    const userStr = localStorage.getItem('user');
                    if (userStr) {
                        const user = JSON.parse(userStr);
                        const actionType = removedCount > 0 ? 'remove_staff_from_event' : 'add_staff_to_event';
                        
                        const logData = {
                            userId: user.id || user.login || 'unknown',
                            userName: user.name || user.login || 'unknown',
                            actionType,
                            date,
                            eventName: ev.event || name,
                            eventId: ev.gId || null,
                            calendarId: ev.calendarId || calendarId || null,
                            oldStaff: oldStaff,
                            newStaff: newStaffArray,
                            details: {
                                removedCount,
                                addedCount: newCount - oldCount - removedCount, // Liczba dodanych (jeśli były dodane)
                            }
                        };

                        // Wywołaj API do logowania (w tle, nie czekaj na odpowiedź)
                        fetch(`${getApiBase()}/api/logEventStaffChange`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(logData)
                        })
                        .catch(() => {
                            // Nie rzucamy błędu - logowanie nie powinno blokować głównej operacji
                        });
                    }
                } catch (err) {
                    // Ignoruj błędy logowania
                }
            }
            if (save) persistAndCache(copy);
            else setCacheForMonths(copy, selectedCalendar, theatreCalendar);
            return copy;
        });
    };

    const updateNeededForEvent = (date, eventRef, newNeeded, calendarId, save = true) => {
        setEventsByDate((prev) => {
            const copy = { ...(prev || {}) };
            const list = Array.isArray(copy[date]) ? [...copy[date]] : [];
            let idx = -1;

            // Nigdy nie używaj indeksu z przefiltrowanego widoku — tylko identyfikator wydarzenia.
            if (typeof eventRef === 'object' && eventRef !== null) {
                idx = resolveEventIndex(list, {
                    gId: eventRef.gId,
                    eventName: eventRef.event || eventRef.name,
                    calendarId: calendarId ?? eventRef.calendarId,
                });
            } else if (typeof eventRef === 'string') {
                idx = resolveEventIndex(list, {
                    eventName: eventRef,
                    calendarId,
                });
            }

            if (idx < 0 || idx >= list.length) {
                return prev;
            }
            const ev = { ...list[idx] };
            const n = Math.max(1, Number(newNeeded) || 1);
            ev.needed = n;
            list[idx] = ev;
            copy[date] = list;
            if (save !== false) persistAndCache(copy);
            return copy;
        });
    };

    const copyStaffToAllDates = async (startDate, eventName, normalizedStaff, needed, calId) => {
        const eventRef = eventRefFromCopyArgs(eventName, calId);
        const sourceStaff = formatStaffListForStore(normalizeStaffEntries(normalizedStaff));
        if (!sourceStaff.length) {
            notifyStaffConflictInfo('Brak obsady do skopiowania.');
            return;
        }

        const siblings = findSiblingEventDays(eventsByDate, startDate, eventRef);
        if (!siblings.length) {
            await requestStaffConflictPrompt({
                mode: 'info',
                title: 'Kopiowanie obsady',
                message: 'Brak innych dni tego wydarzenia (ten sam gId / seria).',
            });
            return;
        }

        const { empty, occupied } = splitCopyTargets(siblings);
        const confirmed = [];

        if (empty.length) {
            const okEmpty = await requestStaffConflictPrompt({
                mode: 'confirm',
                title: 'Kopiuj obsadę',
                message: `Skopiować obsadę z ${formatIsoDayPl(startDate)} na inne dni tego wydarzenia?`,
                confirmQuestion: 'Potwierdzasz skopiowanie na wymienione dni?',
                items: empty.map((item) => ({
                    name: `${formatIsoDayPl(item.date)} — puste`,
                })),
            });
            if (okEmpty) confirmed.push(...empty);
        }

        if (occupied.length) {
            const okOccupied = await requestStaffConflictPrompt({
                mode: 'confirm',
                title: 'Nadpisać istniejącą obsadę?',
                message: 'Te dni już mają obsadę. Bez zgody nie zostaną nadpisane.',
                confirmQuestion: 'Nadpisać istniejącą obsadę na tych dniach?',
                items: occupied.map((item) => ({
                    name: `${formatIsoDayPl(item.date)} — ${item.staffCount} os.`,
                })),
            });
            if (okOccupied) confirmed.push(...occupied);
        }

        if (!confirmed.length) return;

        let anyConflict = false;
        const prepared = [];
        for (const target of confirmed) {
            const { staff: staffToCopy, hadDeclinedConflict } = await resolveStaffForCopy(sourceStaff, {
                date: target.date,
                targetRef: eventRef,
                eventsByDate,
                unavailableByDate,
            });
            if (hadDeclinedConflict) anyConflict = true;
            prepared.push({ date: target.date, staff: staffToCopy });
        }

        setEventsByDate((prev) => {
            let copy = prev || {};
            prepared.forEach((item) => {
                copy = applyStaffCopyToDays(copy, {
                    targets: [{ date: item.date }],
                    eventRef,
                    staff: item.staff,
                    needed,
                });
            });
            persistAndCache(copy);
            return copy;
        });

        if (anyConflict) notifyStaffCopyConflict();
    };

    const toggleUnavailable = (date, personName) => {
        setUnavailableByDate((prev) => {
            const copy = { ...(prev || {}) };
            const arr = Array.isArray(copy[date]) ? [...copy[date]] : [];
            const idx = arr.findIndex((x) =>
                typeof x === 'string' ? x === personName : x?.name === personName
            );
            if (idx === -1) arr.push(personName);
            else arr.splice(idx, 1);
            copy[date] = arr;
            (async () => {
                try {
                    await saveData(undefined, undefined, undefined, copy);
                } catch (e) {
                    // Ignoruj błędy zapisu
                }
            })();
            return copy;
        });
    };

    const addUnavailableRange = (personName, fromDate, toDate) => {
        const name = String(personName || '').trim();
        if (!name || !fromDate || !toDate) return 0;

        const start = new Date(fromDate);
        const end = new Date(toDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;

        const startMs = Math.min(start.getTime(), end.getTime());
        const endMs = Math.max(start.getTime(), end.getTime());
        let affectedDays = 0;
        let nextState = null;

        setUnavailableByDate((prev) => {
            const copy = { ...(prev || {}) };
            let changed = false;
            let localAffectedDays = 0;

            for (let cursorMs = startMs; cursorMs <= endMs; cursorMs += 24 * 3600 * 1000) {
                const dateIso = new Date(cursorMs).toISOString().slice(0, 10);
                const current = Array.isArray(copy[dateIso]) ? [...copy[dateIso]] : [];
                const alreadySet = current.some((entry) => asName(entry) === name);
                if (!alreadySet) {
                    current.push(name);
                    copy[dateIso] = current;
                    changed = true;
                    localAffectedDays += 1;
                }
            }

            if (!changed) return prev;
            affectedDays = localAffectedDays;
            nextState = copy;
            return copy;
        });

        if (nextState) {
            (async () => {
                try {
                    await saveData(undefined, undefined, undefined, nextState);
                } catch {
                    // Ignoruj błędy zapisu
                }
            })();
        }

        return affectedDays;
    };

    // Watch Google Calendar odnawia cron (/api/cron/calendarSync), nie każde otwarcie UI.
    // Otwarcie aplikacji nie woła /api/subscribeCalendar — to tworzyło kanały na adresach wdrożeń.
    // Brak cyklicznego odpytywania Google, listy powiadomień i wersji harmonogramu.
    // Jedyny odczyt zmian jest przy starcie (loadEvents poniżej). Dalsze odświeżenia wymusza cichy push data_update.

    // Zapisz ref do loadEvents dla BroadcastChannel
    useEffect(() => {
        loadEventsRef.current = loadEvents;
    });
    
    // Nasłuchuj na zmiany z innych instancji (BroadcastChannel)
    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') return;
        
        const channel = new BroadcastChannel('planner-updates');
        
        channel.onmessage = (event) => {
            const { type, timestamp, source, sourceId } = event.data || {};
            
            if (sourceId && sourceId === instanceIdRef.current) {
                return;
            }
            
            if (type === 'data-updated') {
                // Nie odświeżaj, jeśli to my właśnie zapisaliśmy zmianę – unikamy nadpisania stanu starą odpowiedzią API (np. po usunięciu osoby z wydarzenia)
                const GRACE_MS = 5000;
                if (lastLocalPersistAtRef.current && (Date.now() - lastLocalPersistAtRef.current) < GRACE_MS) {
                    return;
                }
                // Odśwież dane po krótkim opóźnieniu (aby dać czas na zapis do bazy)
                pendingVersionRef.current = Date.now();
                setTimeout(() => {
                    if (loadEventsRef.current) {
                        loadEventsRef.current(undefined, { forceFetch: false });
                    }
                    // Aktualizuj powiadomienia po odświeżeniu danych (po załadowaniu nowych danych)
                    // Wywołaj po dodatkowym opóźnieniu, aby dać czas na zapis do bazy
                    setTimeout(() => {
                        if (onDataUpdated && typeof onDataUpdated === 'function') {
                            onDataUpdated();
                        }
                    }, 1000);
                }, 2000); // Zwiększ opóźnienie z 1000ms do 2000ms
            }
        };
        
        return () => {
            channel.close();
        };
    }, [onDataUpdated]);

    // Nasłuchuj na wiadomości z Service Workera (push notifications)
    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

        const handleMessage = (event) => {
            const { type, data } = event.data || {};
            
            if (type === 'PUSH_NOTIFICATION_RECEIVED' || type === 'DATA_VERSION_UPDATE') {
                // Odśwież dane po otrzymaniu powiadomienia push
                // Sprawdź, czy powiadomienie dotyczy przypisań (add_staff, remove_staff)
                if (data && (data.type === 'add_staff' || data.type === 'remove_staff' || data.type === 'batch' || data.type === 'data_update' || data.type === 'propose_staff' || data.type === 'proposal_accepted' || data.type === 'proposal_rejected')) {
                    const versionHint = Number(data?.version || 0);
                    const sourceId = data?.sourceId || data?.payload?.sourceId;
                    if (sourceId && sourceId === instanceIdRef.current) {
                        if (versionHint && versionHint > dataVersionRef.current) {
                            dataVersionRef.current = versionHint;
                            try {
                                localStorage.setItem(LS_VERSION, String(versionHint));
                            } catch { }
                        }
                        return;
                    }
                    if (versionHint && versionHint > dataVersionRef.current) {
                        pendingVersionRef.current = versionHint;
                    }
                    const GRACE_MS = 5000;
                    setTimeout(() => {
                        if (lastLocalPersistAtRef.current && (Date.now() - lastLocalPersistAtRef.current) < GRACE_MS) return;
                        if (loadEventsRef.current) {
                            loadEventsRef.current(undefined, { forceFetch: true });
                        }
                        // Lista powiadomień: NotificationsPanel na DATA_VERSION_UPDATE / PUSH.
                        // Drugie getNotifications stąd podwajało odczyt przy każdym pushu.
                    }, 500);
                }
            }
        };

        navigator.serviceWorker.addEventListener('message', handleMessage);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
        };
    }, [onDataUpdated]);

    useEffect(() => { loadEvents(undefined, { forceFetch: true }); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

    return {
        // flags
        loading, dataReady, isSyncing, // isSyncing = true gdy trwa synchronizacja w tle

        // data
        eventsByDate, employees, calendarList, selectedCalendar, theatreCalendar, unavailableByDate,

        // admin ops
        setCalendarListAndSave,
        replaceCalendarsAndReload,
        changeCalendarAndReload,
        changeTheatreCalendar,

        // employees ops
        setEmployees: (next) => {
            const normalized = (next || []).map((e) =>
                typeof e === 'string' ? { name: e, isTheatre: false } : e
            );
            setEmployees(normalized);
            (async () => {
                try {
                    await saveData(undefined, normalized);
                } catch { }
            })();
        },
        updateEmployees: (next) => {
            const normalized = (next || []).map((e) =>
                typeof e === 'string' ? { name: e, isTheatre: false } : e
            );
            setEmployees(normalized);
            (async () => {
                try {
                    await saveData(undefined, normalized);
                } catch { }
            })();
        },
        updateEmployeesAndEvents: (nextEmployees, nextEventsByDate) => {
            const normalized = (nextEmployees || []).map((e) =>
                typeof e === 'string' ? { name: e, isTheatre: false } : e
            );
            setEmployees(normalized);
            if (nextEventsByDate && typeof nextEventsByDate === 'object') {
                setEventsByDate(nextEventsByDate);
                (async () => {
                    try {
                        await saveData(nextEventsByDate, normalized);
                    } catch { }
                })();
                return;
            }
            (async () => {
                try {
                    await saveData(undefined, normalized);
                } catch { }
            })();
        },

        // events ops
        updateEventStaff, updateEventColor, updateNeededForEvent, copyStaffToAllDates, toggleUnavailable, addUnavailableRange,

        // misc
        reloadFromGoogle: (calendarId) => loadEvents(calendarId, { forceFetch: true }),
        firestoreQuotaExceeded,
    };
}

export default usePlannerData;

// ===== Test helpers (pure-ish) =====
// Uwaga: te helpery są tylko do testów jednostkowych; produkcyjnie używamy hooka.
export const __testables = {
    eventsByDateDiffer,
    applyUpdateEventStaff(prevEventsByDate, { date, eventName, newStaff, calendarId }) {
        const normNameLocal = (s) => String(s || '').trim();
        const normLower = (s) => String(s || '').trim().toLowerCase();

        const eventObj = typeof eventName === 'object' ? eventName : null;
        const eventNameStr = eventObj ? (eventObj.event || eventObj.name || '') : String(eventName || '');
        const name = normNameLocal(eventNameStr);
        const gId = eventObj?.gId;

        const prev = { ...(prevEventsByDate || {}) };
        const list = Array.isArray(prev[date]) ? [...prev[date]] : [];

        let idx = -1;
        if (gId && calendarId) {
            idx = list.findIndex((ev) => ev.gId === gId && ev.calendarId === calendarId);
        }
        if (idx === -1 && name) {
            idx = list.findIndex((ev) => normLower(ev.event) === normLower(name) && (calendarId ? ev.calendarId === calendarId : true));
        }
        if (idx === -1) return prevEventsByDate || {};

        const ev = { ...list[idx] };
        const oldStaff = Array.isArray(ev.staff) ? ev.staff : [];
        const newStaffArray = Array.isArray(newStaff) ? [...newStaff] : [];

        const oldCount = oldStaff.length;
        const newCount = newStaffArray.length;
        const removedCount = oldCount - newCount;

        const currentNeeded = Number.isFinite(Number(ev.needed)) && Number(ev.needed) >= 1
            ? Number(ev.needed)
            : Math.max(1, oldCount);

        ev.staff = newStaffArray;

        if (removedCount > 0) {
            // zachowaj needed jako min. poprzednie needed i min. nowy staff
            ev.needed = Math.max(currentNeeded, Math.max(1, newCount));
        } else if (!Number.isFinite(Number(ev.needed)) || Number(ev.needed) < 1) {
            ev.needed = Math.max(1, newCount || 1);
        } else {
            const minNeeded = Math.max(1, newCount);
            if (ev.needed < minNeeded) ev.needed = minNeeded;
        }

        if (calendarId && !ev.calendarId) ev.calendarId = calendarId;
        list[idx] = ev;
        prev[date] = list;
        return prev;
    }
};

